import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
    CheckCircle2,
    RotateCcw,
    Trash2,
    Save,
    AlertTriangle,
    Loader2,
    XCircle,
    Download,
} from "lucide-react";
import BirpStatusBadge from "./BirpStatusBadge";
import BirpQuantitativeSection from "./BirpQuantitativeSection";
import { birpApi } from "../api/birpApi";
import useAuthStore from "@/store/authStore";
import { formatDateUS } from "@/lib/dates";

const SECTIONS = [
    { key: "behavior", label: "Behavior" },
    { key: "intervention", label: "Intervention" },
    { key: "response", label: "Response" },
    { key: "plan", label: "Plan" },
];

const formatDate = formatDateUS;

const isSupervisorPlus = (user) =>
    ["supervisor", "provider_admin", "company_admin", "system_admin"].includes(
        user?.role,
    );

const BirpReportEditor = ({ report, onChange, onDeleted }) => {
    const user = useAuthStore((s) => s.user);
    const [sections, setSections] = useState(report.sections);
    const [saving, setSaving] = useState(false);
    const [approving, setApproving] = useState(false);
    const [rejecting, setRejecting] = useState(false);
    const [downloadingPdf, setDownloadingPdf] = useState(false);
    const [blocks, setBlocks] = useState(null); // prohibited hits from approve error

    // Reset local state when the report changes.
    useEffect(() => {
        setSections(report.sections);
        setBlocks(null);
    }, [report.id, report.sections]);

    const dirty = useMemo(
        () =>
            SECTIONS.some(
                (s) => (sections[s.key] || "") !== (report.sections[s.key] || ""),
            ),
        [sections, report.sections],
    );

    const readOnly = report.status === "approved";
    const canApprove = isSupervisorPlus(user) && report.status !== "approved";

    const handleSectionChange = (key, value) => {
        setSections((prev) => ({ ...prev, [key]: value }));
    };

    const handleReset = () => {
        setSections(report.generated_sections);
        setBlocks(null);
    };

    const handleSave = async () => {
        if (!dirty) return;
        setSaving(true);
        try {
            const patch = {};
            SECTIONS.forEach((s) => {
                if ((sections[s.key] || "") !== (report.sections[s.key] || "")) {
                    patch[s.key] = sections[s.key] || "";
                }
            });
            const updated = await birpApi.update(report.id, patch);
            toast.success("Report saved");
            setBlocks(null);
            onChange(updated);
        } catch (err) {
            toast.error(err?.message || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    const handleApprove = async () => {
        // Save any pending edits first
        try {
            if (dirty) {
                await handleSave();
            }
            setApproving(true);
            const updated = await birpApi.approve(report.id);
            toast.success("Report approved");
            setBlocks(null);
            onChange(updated);
        } catch (err) {
            const data = err?.data?.detail;
            if (data && typeof data === "object" && Array.isArray(data.hits)) {
                setBlocks(data.hits);
                toast.error(data.message || "Approval blocked by prohibited words");
            } else {
                toast.error(err?.message || "Approve failed");
            }
        } finally {
            setApproving(false);
        }
    };

    const handleReject = async () => {
        if (!window.confirm("Reject this report?")) return;
        setRejecting(true);
        try {
            const updated = await birpApi.reject(report.id);
            toast.warning("Report rejected");
            onChange(updated);
        } catch (err) {
            toast.error(err?.message || "Reject failed");
        } finally {
            setRejecting(false);
        }
    };

    const handleDelete = async () => {
        if (!window.confirm("Delete this draft report?")) return;
        try {
            await birpApi.remove(report.id);
            toast.success("Report deleted");
            onDeleted(report.id);
        } catch (err) {
            toast.error(err?.message || "Delete failed");
        }
    };

    const handleDownloadPdf = async () => {
        setDownloadingPdf(true);
        try {
            const { blob, filename } = await birpApi.downloadPdf(report.id);
            // Trigger download in the browser
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            // Revoke a tick later so Safari has time to start the download.
            setTimeout(() => URL.revokeObjectURL(url), 1000);
            toast.success("PDF downloaded");
        } catch (err) {
            toast.error(err?.message || "Download failed");
        } finally {
            setDownloadingPdf(false);
        }
    };

    const hasPeriod =
        report.period_end && report.period_end !== report.report_date;
    const periodLabel = hasPeriod
        ? `${formatDate(report.report_date)} → ${formatDate(report.period_end)}`
        : formatDate(report.report_date);

    return (
        <div
            className="flex h-full flex-col"
            data-testid={`birp-editor-${report.id}`}
        >
            {/* Header */}
            <div className="flex flex-wrap items-start justify-between gap-3 border-b border-slate-200 bg-white px-6 py-4">
                <div className="min-w-0">
                    <div className="flex items-center gap-2">
                        <h2 className="text-lg font-heading font-black tracking-tight text-slate-900">
                            {report.individual_name}
                        </h2>
                        <BirpStatusBadge status={report.status} />
                    </div>
                    <p className="mt-0.5 text-xs text-slate-500">
                        <span data-testid="birp-editor-period">{periodLabel}</span> ·{" "}
                        {report.measurement_count ?? 0} measurements
                        {report.model_used && (
                            <>
                                {" "}
                                ·{" "}
                                <span className="font-mono text-[10px] text-slate-400">
                                    {report.model_used}
                                </span>
                            </>
                        )}
                    </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <button
                        type="button"
                        onClick={handleDownloadPdf}
                        disabled={downloadingPdf}
                        className="inline-flex items-center gap-1 rounded-lg border border-brand-blue-200 bg-brand-blue-50 px-2.5 py-1.5 text-xs font-bold text-brand-blue-800 hover:bg-brand-blue-100 disabled:cursor-not-allowed disabled:opacity-60"
                        data-testid="birp-download-pdf-btn"
                        title="Download this report as a professional PDF"
                    >
                        {downloadingPdf ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                            <Download className="h-3.5 w-3.5" />
                        )}
                        {downloadingPdf ? "Preparing…" : "Download PDF"}
                    </button>
                    {!readOnly && (
                        <>
                            <button
                                type="button"
                                onClick={handleReset}
                                className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50"
                                data-testid="birp-editor-reset"
                                title="Reset to AI-generated version"
                            >
                                <RotateCcw className="h-3.5 w-3.5" />
                                Reset
                            </button>
                            <button
                                type="button"
                                onClick={handleSave}
                                disabled={!dirty || saving}
                                className="inline-flex items-center gap-1 rounded-lg bg-slate-800 px-3 py-1.5 text-xs font-bold text-white hover:bg-slate-900 disabled:cursor-not-allowed disabled:opacity-50"
                                data-testid="birp-editor-save"
                            >
                                {saving ? (
                                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                ) : (
                                    <Save className="h-3.5 w-3.5" />
                                )}
                                Save
                            </button>
                        </>
                    )}
                    {canApprove && (
                        <button
                            type="button"
                            onClick={handleApprove}
                            disabled={approving}
                            className="inline-flex items-center gap-1 rounded-lg bg-green-600 px-3 py-1.5 text-xs font-bold text-white hover:bg-green-700 disabled:opacity-50"
                            data-testid="birp-editor-approve"
                        >
                            {approving ? (
                                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            ) : (
                                <CheckCircle2 className="h-3.5 w-3.5" />
                            )}
                            Approve
                        </button>
                    )}
                    {canApprove && report.status !== "rejected" && (
                        <button
                            type="button"
                            onClick={handleReject}
                            disabled={rejecting}
                            className="inline-flex items-center gap-1 rounded-lg border border-red-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-red-700 hover:bg-red-50"
                            data-testid="birp-editor-reject"
                        >
                            <XCircle className="h-3.5 w-3.5" />
                            Reject
                        </button>
                    )}
                    {!readOnly && (
                        <button
                            type="button"
                            onClick={handleDelete}
                            className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-500 hover:bg-red-50 hover:text-red-700"
                            data-testid="birp-editor-delete"
                            title="Delete draft"
                        >
                            <Trash2 className="h-3.5 w-3.5" />
                        </button>
                    )}
                </div>
            </div>

            {/* Prohibited words banner */}
            {blocks && blocks.length > 0 && (
                <div
                    className="mx-6 mt-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2"
                    data-testid="birp-editor-blocked-banner"
                >
                    <div className="flex items-center gap-2 text-xs font-bold text-red-800">
                        <AlertTriangle className="h-4 w-4" />
                        Approval blocked by prohibited language.
                    </div>
                    <p className="mt-1 text-[11px] text-red-700">
                        Remove or rephrase:{" "}
                        {blocks.map((h) => h.word).join(", ")}
                    </p>
                </div>
            )}

            {/* Sections */}
            <div className="flex-1 space-y-4 overflow-y-auto bg-slate-50 px-6 py-6">
                {/* Quantitative Summary (KPIs + radar + trend + breakdowns) */}
                <BirpQuantitativeSection
                    quantitative={report.quantitative}
                    periodStart={report.report_date}
                    periodEnd={report.period_end}
                />
                {SECTIONS.map((s) => {
                    const original = report.generated_sections?.[s.key] || "";
                    const value = sections[s.key] || "";
                    const changed = value !== original;
                    return (
                        <div
                            key={s.key}
                            className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm"
                            data-testid={`birp-section-${s.key}`}
                        >
                            <div className="mb-2 flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <span className="text-[10px] font-black uppercase tracking-widest text-brand-blue-700">
                                        {s.label}
                                    </span>
                                    {changed && !readOnly && (
                                        <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-amber-800">
                                            Edited
                                        </span>
                                    )}
                                </div>
                                <span className="text-[10px] text-slate-400">
                                    {value.length} chars
                                </span>
                            </div>
                            <textarea
                                value={value}
                                onChange={(e) =>
                                    handleSectionChange(s.key, e.target.value)
                                }
                                readOnly={readOnly}
                                rows={5}
                                data-testid={`birp-section-textarea-${s.key}`}
                                className={`w-full resize-y rounded-lg border p-3 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-brand-blue-500/30 ${
                                    readOnly
                                        ? "border-slate-100 bg-slate-50 text-slate-600"
                                        : "border-slate-200 bg-white"
                                }`}
                            />
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default BirpReportEditor;
